import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Github,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Loader2,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { apiService } from "@/lib/api";
import { logger } from "@/lib/logger";
import { env } from "@/config/env";
import { isValidGitHubUrl, normalizeGitHubUrl } from "@/lib/utils";

const compactAgentSchema = z.object({
  githubUrl: z
    .string()
    .url("Please enter a valid URL")
    .refine(
      (url) => isValidGitHubUrl(url),
      "Please enter a valid GitHub repository URL in format: https://github.com/owner/repo",
    ),
});

type CompactAgentFormSchema = z.infer<typeof compactAgentSchema>;

const SubmitAgentCompact: React.FC = () => {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>("");
  const [urlValidation, setUrlValidation] = useState<{
    isValid?: boolean;
    message?: string;
    isChecking?: boolean;
    existingAgentId?: string; // For storing existing agent ID when duplicate is found
    redirecting?: boolean; // For showing redirect status
  }>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<CompactAgentFormSchema>({
    resolver: zodResolver(compactAgentSchema),
  });

  const githubUrl = watch("githubUrl");

  // Enhanced URL validation with automatic redirect for duplicates
  React.useEffect(() => {
    if (!githubUrl || errors.githubUrl) {
      setUrlValidation({});
      return;
    }

    const validateUrl = async () => {
      setUrlValidation({
        isChecking: true,
        message: "Validating GitHub URL...",
      });

      try {
        // First validate the URL format client-side
        if (!isValidGitHubUrl(githubUrl)) {
          setUrlValidation({
            isValid: false,
            message:
              "Invalid GitHub URL format. Use: https://github.com/owner/repo",
          });
          return;
        }

        // Normalize URL for comparison
        const normalizedUrl = normalizeGitHubUrl(githubUrl);

        // Check for duplicates by searching existing agents
        const response = await apiService.getAgents(1000);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const existingAgent = response.agents.find((agent: any) => {
          const agentGithubUrl = agent.githubUrl || agent.github_url;
          if (!agentGithubUrl) return false;

          const normalizedAgentUrl = normalizeGitHubUrl(agentGithubUrl);

          return normalizedAgentUrl === normalizedUrl;
        });

        if (existingAgent) {
          // Set initial duplicate state
          setUrlValidation({
            isValid: false,
            message: `Duplicate detected! Redirecting to existing agent...`,
            existingAgentId: existingAgent.id,
            redirecting: true,
          });

          // Auto-redirect after a short delay to show the message
          setTimeout(() => {
            setUrlValidation((prev) => ({
              ...prev,
              message: `Redirecting to "${existingAgent.title}"... (Click here if not redirected)`,
            }));

            // Perform the redirect
            setTimeout(() => {
              navigate(`/agent/${existingAgent.id}`);
            }, 500);
          }, 1500); // Show duplicate message for 1.5 seconds before redirecting
        } else {
          setUrlValidation({
            isValid: true,
            message: "Valid GitHub URL ✓",
          });
        }
      } catch (error) {
        logger.error("URL validation error:", error);
        setUrlValidation({
          isValid: false,
          message: "Could not validate repository. Please check the URL.",
        });
      }
    };

    const timeoutId = setTimeout(validateUrl, 800);
    return () => clearTimeout(timeoutId);
  }, [githubUrl, errors.githubUrl, navigate]);

  const onSubmit = async (data: CompactAgentFormSchema) => {
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(true);
    setProcessingStatus("Validating GitHub URL...");

    try {
      // Check for duplicates one more time before submission
      if (urlValidation.existingAgentId) {
        // Redirect immediately to existing agent
        navigate(`/agent/${urlValidation.existingAgentId}`);
        return;
      }

      // Call the GitHub URL Handler Lambda
      setProcessingStatus("Analyzing repository with AI...");

      const response = await fetch(`${env.API_BASE_URL}/submit-github`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": env.API_KEY,
        },
        body: JSON.stringify({ githubUrl: data.githubUrl }),
      });

      const result = await response.json();

      // Handle HTTP status codes
      if (!response.ok) {
        handleSubmissionError(result);
        return;
      }

      // Handle successful response (200)
      if (result.success && result.agent_id) {
        // Agent was created successfully
        setProcessingStatus("✅ Agent created successfully!");
        setSubmitSuccess(true);

        // Reset form
        reset();
        setUrlValidation({});

        // Redirect to agent detail page after a short delay
        setTimeout(() => {
          navigate(`/agent/${result.agent_id}`);
        }, 2000);
      } else if (result.duplicate && result.existing_agent_id) {
        // Handle duplicate found by backend - redirect immediately
        navigate(`/agent/${result.existing_agent_id}`);
        return;
      } else if (result.processing) {
        // Handle 202 - Processing started (asynchronous mode)
        setProcessingStatus("Analysis in progress...");
        setSubmitSuccess(true);

        // Reset form
        reset();
        setUrlValidation({});

        // Show processing message instead of redirecting
        setTimeout(() => {
          navigate("/");
        }, 5000);
      } else if (!result.success) {
        // Handle business logic errors returned with success: false
        // Map these to our error handling system for consistent UI experience
        if (result.error) {
          handleSubmissionError(result);
          return;
        } else {
          // Fallback for unmapped success: false responses
          throw new Error(result.message || "Repository analysis failed");
        }
      }
    } catch (error) {
      logger.error("Error submitting agent:", error);

      let errorMessage = "An unexpected error occurred. Please try again.";

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
      if (!submitSuccess) {
        setProcessingStatus("");
      }
    }
  };

  // Helper function to handle submission errors
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmissionError = (result: any) => {
    let errorMessage = "An unexpected error occurred. Please try again.";

    // Handle specific error codes from the Lambda
    switch (result.error) {
      case "missing_url":
        errorMessage =
          "GitHub URL is required. Please provide a valid repository URL.";
        break;
      case "invalid_url":
        errorMessage =
          "Please provide a valid GitHub repository URL in the format: https://github.com/owner/repo";
        break;
      case "repository_not_found":
        errorMessage =
          "🔍 Repository Not Found\n\nThe repository could not be accessed. Please ensure:\n• The repository exists and is public\n• The URL is correct\n• You have the right permissions if it's private\n\nExample: https://github.com/username/repository-name";
        break;
      case "analysis_failed":
        errorMessage =
          "⚠️ Analysis Failed\n\nOur AI system couldn't analyze the repository. This might be due to:\n• Repository is temporarily unavailable\n• Large repository size causing timeout\n• Network connectivity issues\n\n💡 Try again in a few minutes, or contact support if the issue persists.";
        break;
      case "not_ai_infrastructure": {
        // Create a detailed, user-friendly message based on the AI analysis
        let customMessage =
          "🤖 AI Analysis Result: This repository doesn't appear to be focused enough on intelligent Infrastructure Agents or agentic infrastructure.";

        // Add specific details from the AI analysis if available
        if (result.details) {
          const details = result.details.toLowerCase();

          // Provide specific guidance based on the analysis
          if (
            details.includes("web") ||
            details.includes("frontend") ||
            details.includes("react") ||
            details.includes("vue") ||
            details.includes("angular")
          ) {
            customMessage +=
              "\n\n📋 Detected: Web/Frontend Application\n💡 Suggestion: This platform focuses on infrastructure automation tools, not web applications. Consider submitting repositories that automate cloud infrastructure, DevOps pipelines, or infrastructure management.";
          } else if (details.includes("game") || details.includes("gaming")) {
            customMessage +=
              "\n\n🎮 Detected: Gaming/Entertainment Project\n💡 Suggestion: This platform is designed for infrastructure automation tools. Try submitting repositories that help automate server provisioning, deployment pipelines, or infrastructure monitoring.";
          } else if (
            details.includes("mobile") ||
            details.includes("android") ||
            details.includes("ios")
          ) {
            customMessage +=
              "\n\n📱 Detected: Mobile Application\n💡 Suggestion: This platform focuses on infrastructure automation. Consider submitting tools that automate mobile app deployment, CI/CD pipelines, or infrastructure management for mobile backends.";
          } else if (
            details.includes("data") ||
            details.includes("analytics") ||
            details.includes("ml") ||
            details.includes("machine learning")
          ) {
            customMessage += `\n\n📊 Detected: Data/ML Project\n💡 Suggestion: While this appears to be data-related, we're looking for infrastructure automation tools. Consider submitting MLOps platforms, automated data pipeline tools, or infrastructure automation for ML workloads.`;
          } else if (
            details.includes("library") ||
            details.includes("package") ||
            details.includes("sdk")
          ) {
            customMessage += `\n\n📚 Detected: Library/Package\n💡 Suggestion: This appears to be a general-purpose library. We're looking for complete infrastructure automation solutions, orchestration tools, or platforms that automate infrastructure management.`;
          } else {
            customMessage += `\n\n Suggestion: We're looking for repositories that enable autonomous or intelligent infrastructure management—such as provisioning, orchestration, monitoring, remediation, governance, or optimization—using AI, advanced reasoning, or agentic automation.`;
          }
        }

        errorMessage = customMessage;
        break;
      }
      case "duplicate_url":
        // Handle duplicate found by backend
        if (result.existing_agent_id) {
          navigate(`/agent/${result.existing_agent_id}`);
          return;
        }
        errorMessage =
          "This repository has already been submitted. Please check existing agents.";
        break;
      case "creation_failed":
        errorMessage =
          "Failed to create agent record. This repository may already be submitted or there was a database error.";
        break;
      case "timeout":
        errorMessage = `⏱️ Analysis Taking Longer Than Expected\n\nYour repository is being processed in the background. Complex repositories can take up to 5 minutes to analyze.\n\n✅ What's happening:\n• AI is analyzing your repository structure\n• Extracting metadata and determining relevance\n• This process will complete automatically\n\n💡 Check back in a few minutes to see your agent in the portal!`;
        break;
      case "validation_failed":
        errorMessage = `❌ Validation Failed\n\n${result.details || "Repository validation failed. Please check the repository and try again."}\n\n💡 Common issues:\n• Repository URL format incorrect\n• Repository is private or doesn't exist\n• Repository contains no recognizable code structure`;
        break;
      case "internal_error":
        errorMessage =
          "An internal error occurred. Please try again in a few moments.";
        break;
      default:
        // Use the message from the response if available
        errorMessage = result.message || result.details || errorMessage;
    }

    setSubmitError(errorMessage);
  };

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12">
          <div className="mx-auto max-w-2xl text-center">
            <div className="rounded-lg border bg-card p-12">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold mb-4">
                Repository Submitted Successfully!
              </h2>
              {processingStatus === "✅ Agent created successfully!" ? (
                <p className="text-muted-foreground mb-6">
                  Your AI infrastructure agent has been analyzed and added to
                  the portal. You'll be redirected to the agent page shortly.
                </p>
              ) : (
                <div className="space-y-4">
                  <p className="text-muted-foreground mb-4">
                    Your repository is being analyzed by our AI system. This
                    process may take up to 3 minutes for complex repositories.
                  </p>
                  <div className="rounded-lg legal-page-notice-box p-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-left">
                        <h3 className="font-medium legal-page-notice-title mb-1">
                          What's happening now?
                        </h3>
                        <ul className="text-sm legal-page-notice-text space-y-1">
                          <li>
                            • AI is analyzing your repository structure and
                            content
                          </li>
                          <li>
                            • Extracting project metadata and determining
                            relevance
                          </li>
                          <li>
                            • Generating appropriate tags and competencies
                          </li>
                          <li>• Creating the agent record in our database</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    You'll be redirected to the homepage shortly. Check back in
                    a few minutes to see your agent!
                  </p>
                </div>
              )}
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <style>{`
        @keyframes progress {
          0% { width: 0%; }
          50% { width: 60%; }
          100% { width: 100%; }
        }
      `}</style>
      <div className="container mx-auto px-4 py-12">
        <div className="mx-auto max-w-2xl">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <div className="p-3 rounded-full bg-gradient-to-r from-orange-500 to-red-500">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Share an Infrastructure Agent
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Contribute to the ecosystem by sharing agentic infrastructure
              projects from GitHub.
            </p>
          </div>

          <div className="rounded-lg border bg-card shadow-lg">
            <form onSubmit={handleSubmit(onSubmit)} className="p-8">
              <div className="space-y-6">
                {/* GitHub URL Input */}
                <div>
                  <label
                    htmlFor="githubUrl"
                    className="block text-sm font-medium mb-3"
                  >
                    <div className="flex items-center gap-2">
                      <Github className="h-4 w-4" />
                      GitHub Repository URL *
                    </div>
                  </label>
                  <div className="relative">
                    <input
                      {...register("githubUrl")}
                      type="url"
                      id="githubUrl"
                      placeholder="https://github.com/username/agentic-infrastructure"
                      className="flex h-12 w-full rounded-md border-2 border-input bg-background px-4 py-3 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-orange-500 focus:border-orange-500 disabled:cursor-not-allowed disabled:opacity-50 pr-10"
                      disabled={isSubmitting}
                    />
                    {urlValidation.isChecking && (
                      <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    {urlValidation.isValid === true &&
                      !urlValidation.isChecking && (
                        <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
                      )}
                    {urlValidation.isValid === false &&
                      !urlValidation.isChecking && (
                        <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-red-500" />
                      )}
                  </div>

                  {/* Validation Messages */}
                  {errors.githubUrl && (
                    <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.githubUrl.message}
                    </p>
                  )}

                  {urlValidation.message && !errors.githubUrl && (
                    <div
                      className={`text-sm mt-2 flex items-center gap-1 ${
                        urlValidation.isValid === true
                          ? "text-green-600"
                          : urlValidation.isValid === false &&
                              urlValidation.redirecting
                            ? "text-blue-600"
                            : urlValidation.isValid === false
                              ? "text-red-500"
                              : "text-blue-500"
                      }`}
                    >
                      {urlValidation.isChecking ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : urlValidation.redirecting ? (
                        <ArrowRight className="h-3 w-3 animate-pulse" />
                      ) : urlValidation.isValid === true ? (
                        <CheckCircle className="h-3 w-3" />
                      ) : urlValidation.isValid === false ? (
                        <AlertCircle className="h-3 w-3" />
                      ) : null}

                      {urlValidation.existingAgentId &&
                      !urlValidation.redirecting ? (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/agent/${urlValidation.existingAgentId}`)
                          }
                          className="text-red-500 hover:text-red-600 underline cursor-pointer"
                        >
                          {urlValidation.message}
                        </button>
                      ) : urlValidation.existingAgentId &&
                        urlValidation.redirecting ? (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-1">
                            <span className="text-blue-600">
                              {urlValidation.message}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-full bg-blue-100 rounded-full h-1.5">
                              <div
                                className="bg-blue-600 h-1.5 rounded-full animate-pulse"
                                style={{
                                  animation: "progress 2s ease-in-out",
                                }}
                              ></div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/agent/${urlValidation.existingAgentId}`,
                              )
                            }
                            className="text-blue-500 hover:text-blue-600 underline cursor-pointer text-xs"
                          >
                            Click here if not redirected automatically
                          </button>
                        </div>
                      ) : (
                        <span>{urlValidation.message}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Error Display */}
                {submitError && (
                  <div className="rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-medium text-red-900 dark:text-red-100 mb-1">
                          Submission Error
                        </h3>
                        <p className="text-sm text-red-700 dark:text-red-200 whitespace-pre-line">
                          {submitError}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    urlValidation.isValid === false ||
                    !!errors.githubUrl ||
                    !!urlValidation.existingAgentId ||
                    urlValidation.redirecting
                  }
                  onClick={(e) => {
                    if (urlValidation.existingAgentId) {
                      e.preventDefault();
                      navigate(`/agent/${urlValidation.existingAgentId}`);
                    }
                  }}
                  className="w-full inline-flex items-center justify-center rounded-md bg-gradient-to-r from-orange-600 to-red-600 px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:from-orange-700 hover:to-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {processingStatus || "Processing..."}
                    </>
                  ) : urlValidation.redirecting ? (
                    <>
                      <ArrowRight className="mr-2 h-4 w-4 animate-pulse" />
                      Redirecting...
                    </>
                  ) : urlValidation.existingAgentId ? (
                    <>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      View Existing Agent
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Submit Infrastructure Agent
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </button>

                {/* Processing Status Display */}
                {isSubmitting && processingStatus && (
                  <div className="text-center space-y-3">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                      <p className="text-sm font-medium text-foreground">
                        {processingStatus}
                      </p>
                    </div>
                    {processingStatus.includes("Analyzing") && (
                      <p className="text-xs text-muted-foreground">
                        This may take up to 3 minutes for complex
                        repositories...
                      </p>
                    )}
                  </div>
                )}

                {/* AI Analysis Info */}
                <div className="rounded-lg legal-page-notice-box p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                    <div>
                      <h3 className="font-medium mb-1 legal-page-notice-title">
                        Automated Repository Analysis
                      </h3>
                      <p className="text-sm legal-page-notice-text">
                        The agent scans repositories to extract metadata and
                        validate alignment with agentic infrastructure
                        principles. Analysis typically completes within 3
                        minutes.
                      </p>
                      <div className="mt-2 text-xs legal-page-notice-text">
                        <strong>Requirement:</strong> Repository must relate to
                        Infrastructure Agents (e.g., projects for intelligent
                        automation, orchestration, monitoring, or governance of
                        cloud and on-premises environments).
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitAgentCompact;
